import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import useSEO from "../hooks/useSEO";
import profilo from "../img/chi_sono.webp";
import "./ChiSono.css";

const percorso = [
  {
    titolo: "Laurea Triennale",
    descrizione: "Scienze Psicologiche Applicate, Università de L'Aquila.",
    completato: true,
  },
  {
    titolo: "Laurea Magistrale",
    descrizione: "Psicologia Clinica e della Salute, Università de L'Aquila.",
    completato: true,
  },
  {
    titolo: "Esperienza con minori",
    descrizione: "Supporto specialistico per minori con difficoltà familiare.",
    completato: true,
  },
  {
    titolo: "Libera professione",
    descrizione: "Attività clinica con adolescenti e adulti.",
    completato: false,
  },
  {
    titolo: "Psicoterapeuta in formazione",
    descrizione:
      "Scuola di specializzazione in psicoterapia analitico-archetipico Atanor (2° anno in corso).",
    completato: false,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const ChiSono = () => {
  useSEO({
    title: "La Psicologa Anna | Chi Sono",
    description:
      "Scopri la formazione e l'approccio della Dott.ssa Anna Antoniozzi, psicologa clinica a Rieti.",
    canonicalPath: "/chi-sono",
  });

  return (
    <div className="chisono">
      {/* HERO */}
      <section className="section section--paper chisono-hero">
        <div className="container chisono-hero__inner">
          <motion.div
            className="chisono-hero__text"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="eyebrow">La professionista</p>
            <h1 className="display chisono-hero__title">Ciao, sono Anna</h1>
            <p className="lead">
              Psicologa iscritta all'Albo del Lazio. Credo in una relazione di
              cura fondata sull'ascolto, in cui trovare lo spazio per capirsi e
              cambiare.
            </p>
            <div className="chips chisono-chips">
              <span className="chip">Albo Psicologi del Lazio</span>
              <span className="chip">Approccio junghiano</span>
              <span className="chip">In presenza &amp; online</span>
            </div>
          </motion.div>

          <motion.div
            className="chisono-hero__media"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          >
            <div className="chisono-photo">
              <img
                src={profilo}
                alt="Dott.ssa Anna Antoniozzi, psicologa clinica a Rieti"
                loading="eager"
                width="1400"
                height="1050"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* BIO */}
      <section className="section section--cream">
        <div className="container container--narrow chisono-bio">
          <Reveal>
            <p>
              La mia formazione ha avuto inizio presso l'Università dell'Aquila,
              dove ho conseguito la laurea triennale in Scienze Psicologiche
              Applicate e, successivamente, la laurea magistrale in Psicologia
              Clinica e della Salute.
            </p>
          </Reveal>
          <Reveal>
            <blockquote className="pull-quote">
              «Il mio approccio è orientato al potenziamento del benessere:
              usare al meglio le proprie risorse cognitive ed emotive per
              affrontare le sfide, costruire relazioni soddisfacenti e adattarsi
              in modo costruttivo.»
            </blockquote>
          </Reveal>
          <Reveal>
            <p>
              Attualmente sono psicoterapeuta in formazione al secondo anno
              presso la Scuola Atanor: un approccio fondato sulla psicologia
              analitica di Carl Gustav Jung e sulla psicologia archetipica di
              James Hillman, che offre una visione della psiche profonda,
              simbolica e orientata alla trasformazione.
            </p>
          </Reveal>
          <Reveal>
            <p>
              Dopo la laurea ho lavorato nel supporto specialistico per minori
              in difficoltà familiare, un'esperienza che ha arricchito la mia
              pratica. Parallelamente ho intrapreso la libera professione,
              occupandomi di prevenzione, valutazione e supporto psicologico per
              adolescenti e adulti.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="section section--paper">
        <div className="container">
          <Reveal className="section-head">
            <p className="eyebrow">Formazione ed esperienza</p>
            <h2 className="display--md">Il mio percorso</h2>
          </Reveal>
          <motion.div
            className="timeline"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={containerVariants}
          >
            {percorso.map((step, i) => (
              <motion.div
                key={i}
                className="timeline-item"
                variants={itemVariants}
              >
                <span
                  className={`timeline-dot ${step.completato ? "done" : "current"}`}
                />
                <div className="timeline-body">
                  <div className="timeline-head">
                    <h3 className="timeline-title">{step.titolo}</h3>
                    <span
                      className={`timeline-badge ${step.completato ? "done" : "current"}`}
                    >
                      {step.completato ? "Completato" : "In corso"}
                    </span>
                  </div>
                  <p className="timeline-desc">{step.descrizione}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-band">
        <div className="container cta-band__inner">
          <Reveal>
            <h2 className="display cta-band__title">
              Facciamo il primo passo insieme.
            </h2>
            <p className="cta-band__text">
              Prenota un colloquio conoscitivo o scrivimi per qualsiasi domanda.
            </p>
            <div className="cta-band__actions">
              <Link to="/prenota-appuntamento" className="btn btn--accent">
                Prenota <ArrowRight className="btn-arrow" size={18} />
              </Link>
              <Link to="/contatti" className="btn btn--ghost">
                Contatti
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ChiSono;
